/**
 * @fileoverview Sales Performance page.
 * @description A read-only view of how the sales organisation is doing against its targets for a
 * period, served by the backend's Sales Performance APIs (`/sales/performance/summary`,
 * `/ranking`, `/pending` and `/export`). The backend decides what the caller may see: a Super Admin
 * or Sales Head sees the organisation and may narrow it to one team, a Manager sees their team, an
 * Executive sees only themselves, and only a Super Admin may export. The page therefore draws what
 * the summary's scope allows and never works the numbers out in the browser. The KPIs, charts and
 * team bars come from the summary; the ranking and the pending targets load on their own so one
 * failing read leaves the rest on screen. Export CSV downloads the file, or shows the backend's
 * refusal (including the fresh sign-in the export needs) below the header.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import {
  PageLoadError,
  SalesPerformanceContent,
  SalesPerformanceFilters,
  SalesRequestError,
  SCOPE_LABEL_KEY,
  useSalesPerformanceScreen,
} from '@/features/sales'
import KpiStripSkeleton from '@/shared/components/molecules/KpiStripSkeleton'
import PageHeader from '@/shared/components/molecules/PageHeader'

/**
 * @component
 */
function SalesPerformancePage() {
  const { t } = useTranslation()
  const screen = useSalesPerformanceScreen()
  const { summaryQuery, exportMutation } = screen
  const summary = summaryQuery.data

  const header = (
    <PageHeader
      title={t('nav.salesPerformance')}
      description={
        summary ? t(SCOPE_LABEL_KEY[summary.scope.kind]) : t('sales.salesPerformance.description')
      }
      filters={
        <SalesPerformanceFilters
          summary={screen.optionsSummary}
          period={screen.period}
          team={screen.team}
          isExporting={exportMutation.isPending}
          onPeriodChange={screen.changePeriod}
          onTeamChange={screen.changeTeam}
          onExport={screen.startExport}
        />
      }
    />
  )

  if (!summary) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {summaryQuery.isError ? (
          <PageLoadError
            message={summaryQuery.error.message || t('sales.salesPerformance.loadError')}
            testId="sales-performance-load-error"
          />
        ) : (
          <KpiStripSkeleton />
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {header}
      {exportMutation.error && <SalesRequestError error={exportMutation.error} />}
      {screen.exportedFile && (
        <p
          role="status"
          className="text-compact text-teal"
          data-testid="sales-performance-export-note"
        >
          {t('sales.salesPerformance.exported', { filename: screen.exportedFile })}
        </p>
      )}
      <SalesPerformanceContent
        summary={summary}
        query={screen.query}
        isStale={summaryQuery.isPlaceholderData}
      />
    </div>
  )
}

export default SalesPerformancePage
